import React from "react";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import {
  COMPLETE_RUNS_PERMISSION,
  DUPLICATE_ACCESSION_ERROR_TEXT,
  ID_LIS_MODULE_NAME,
  PRINT_PLATES_PERMISSION,
  VIEW_CREATE_RUN_PERMISSION_PAGE,
  accessionPanelStyle,
  accessionPanelTitleStyle,
  buildDuplicateAccessionErrors,
  buildWellLookup,
  formatAssignTestCellText,
  getColumnSpansPerAccession,
  normalizeWellKey,
  plateGridTableStyle,
  plateGridWrapperStyle,
  plateHeaderCellStyle,
  plateRowHeaderCellStyle,
  plateWellCellStyle,
  type RunGridRow,
  usesTwoAccessionsPerColumn,
} from "../viewCreateRunHelpers";

type TranslationFn = (value: string) => string;

type RunDetailPanelProps = {
  row: RunGridRow;
  tabIndex: number;
  t: TranslationFn;
  runAccessionDrafts: Record<string, string[]>;
  runAccessionErrors: Record<string, Record<number, string>>;
  setRunAccessionDrafts: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setRunAccessionErrors: React.Dispatch<
    React.SetStateAction<Record<string, Record<number, string>>>
  >;
  onApplyAccessions: (row: RunGridRow) => void;
  onPrintPlate: (row: RunGridRow) => void;
  onCompleteRun: (row: RunGridRow) => void;
  isApplyAccessionsLoading: boolean;
  isCompleteRunLoading: boolean;
};

const RunDetailPanel: React.FC<RunDetailPanelProps> = ({
  row,
  tabIndex,
  t,
  runAccessionDrafts,
  runAccessionErrors,
  setRunAccessionDrafts,
  setRunAccessionErrors,
  onApplyAccessions,
  onPrintPlate,
  onCompleteRun,
  isApplyAccessionsLoading,
  isCompleteRunLoading,
}) => {
  const isReadOnlyTab = tabIndex === 1 || tabIndex === 2;
  const wellLookup = buildWellLookup(row.details);
  const rows = row.rows.length
    ? row.rows
    : ["A", "B", "C", "D", "E", "F", "G", "H"];
  const columns = row.columns.length
    ? row.columns
    : Array.from({ length: 12 }, (_, index) => index + 1);
  const isTwoAccessionsPerColumn = usesTwoAccessionsPerColumn(
    rows.length,
    columns.length,
    row.noOfAccession
  );
  const columnCountForBands = columns.length;
  const accessionColumnSpans = getColumnSpansPerAccession(
    columnCountForBands,
    row.noOfAccession
  );
  const accessionSlots =
    runAccessionDrafts[row.id] ?? row.accessionSlots;

  return (
    <div
      className="p-4"
      style={{
        background: "#fafbfc",
        borderTop: "1px solid #eff2f5",
      }}
    >
      <div className="fw-bold mb-3 fs-6" style={{ color: "#3F4254" }}>
        {t("Run Detail")}
      </div>
      <div
        id={`IDViewCreateRunPlateDetail_${row.id}`}
        style={accessionPanelStyle}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div style={accessionPanelTitleStyle}>{t("Palate Detail")}</div>
          <div className="d-flex gap-2">
            {!isReadOnlyTab && (
              <button
                id={`IDViewCreateRunApplyAccessions_${row.id}`}
                type="button"
                className="btn-primary--icon px-7 text-white fw-500 rounded"
                style={{
                  backgroundColor: "#69A54B",
                  border: "none",
                  padding: "0.65rem 1.75rem",
                  cursor: "pointer",
                }}
                onClick={() => onApplyAccessions(row)}
              >
                {isApplyAccessionsLoading ? t("Applying...") : t("Apply Accessions")}
              </button>
            )}
            <PermissionComponent
              moduleName={ID_LIS_MODULE_NAME}
              pageName={VIEW_CREATE_RUN_PERMISSION_PAGE}
              permissionIdentifier={PRINT_PLATES_PERMISSION}
            >
              <button
                id={`IDViewCreateRunPrintPlate_${row.id}`}
                type="button"
                className="btn-primary--icon px-7 text-white fw-500 rounded"
                style={{
                  backgroundColor: "#4A5699",
                  border: "none",
                  padding: "0.65rem 1.75rem",
                  cursor: "pointer",
                }}
                onClick={() => onPrintPlate(row)}
              >
                {t("Print Plate")}
              </button>
            </PermissionComponent>
            {!isReadOnlyTab && (
              <PermissionComponent
                moduleName={ID_LIS_MODULE_NAME}
                pageName={VIEW_CREATE_RUN_PERMISSION_PAGE}
                permissionIdentifier={COMPLETE_RUNS_PERMISSION}
              >
                <button
                  id={`IDViewCreateRunCompleteRun_${row.id}`}
                  type="button"
                  className="btn-primary--icon px-7 text-white fw-500 rounded"
                  style={{
                    backgroundColor: "#69A54B",
                    border: "none",
                    padding: "0.65rem 1.75rem",
                    cursor: "pointer",
                  }}
                  disabled={isCompleteRunLoading}
                  onClick={() => onCompleteRun(row)}
                >
                  <span>{isCompleteRunLoading ? t("Completing...") : t("Complete Run")}</span>
                </button>
              </PermissionComponent>
            )}
          </div>
        </div>
        <div className="row g-3 mb-4">
          {accessionSlots.map((acc, idx) => {
            const errorText = isReadOnlyTab
              ? undefined
              : runAccessionErrors[row.id]?.[idx];

            return (
              <div
                key={`acc-slot-${row.id}-${idx}`}
                className={
                  row.noOfAccession <= 4
                    ? "col-12 col-sm-6 col-md-4"
                    : row.noOfAccession <= 6
                      ? "col-12 col-sm-6 col-md-4"
                      : row.noOfAccession <= 12
                        ? "col-6 col-sm-4 col-md-3"
                        : row.noOfAccession <= 24
                          ? "col-6 col-sm-4 col-md-2"
                          : "col-6 col-sm-3 col-md-2"
                }
              >
                <label className="form-label fs-8 fw-semibold mb-1 text-gray-600">
                  {t("Accession")} {idx + 1}
                </label>
                <input
                  id={`IDViewCreateRunAccession_${row.id}_${idx}`}
                  type="text"
                  className="form-control bg-white h-38px rounded-2 fs-7"
                  style={{
                    border: errorText ? "1px solid #DC3545" : "1px solid #D3D3D3",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontSize: "14px",
                    color: "#3F4254",
                    boxShadow: "none",
                    background: isReadOnlyTab ? "#F3F4F6" : "#FFFFFF",
                  }}
                  readOnly={isReadOnlyTab}
                  disabled={isReadOnlyTab}
                  value={acc}
                  onChange={(e) => {
                    if (isReadOnlyTab) return;
                    const v = e.target.value;
                    setRunAccessionDrafts((prev) => {
                      const base = prev[row.id] ?? [...row.accessionSlots];
                      const next = [...base];
                      next[idx] = v;

                      const duplicateErrors = buildDuplicateAccessionErrors(
                        next,
                        DUPLICATE_ACCESSION_ERROR_TEXT
                      );

                      setRunAccessionErrors((prevErrors) => {
                        const currentRowErrors = { ...(prevErrors[row.id] ?? {}) };
                        const nonDuplicateErrors = Object.entries(
                          currentRowErrors
                        ).reduce<Record<number, string>>((accumulator, [key, message]) => {
                          if (message !== DUPLICATE_ACCESSION_ERROR_TEXT) {
                            accumulator[Number(key)] = message;
                          }
                          return accumulator;
                        }, {});

                        return {
                          ...prevErrors,
                          [row.id]: {
                            ...nonDuplicateErrors,
                            ...duplicateErrors,
                          },
                        };
                      });

                      return { ...prev, [row.id]: next };
                    });
                  }}
                />
                {errorText && (
                  <div className="text-danger fs-8 mt-1">{t(errorText)}</div>
                )}
              </div>
            );
          })}
        </div>

        <div style={plateGridWrapperStyle}>
          <table style={plateGridTableStyle}>
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  style={{
                    ...plateRowHeaderCellStyle,
                    verticalAlign: "middle",
                  }}
                />
                {isTwoAccessionsPerColumn
                  ? columns.map((column) => {
                    const firstIndex = (column - 1) * 2;
                    return (
                      <th
                        key={`acc-col-pair-${row.id}-${column}`}
                        style={plateHeaderCellStyle}
                      >
                        <div>{accessionSlots[firstIndex]?.trim() || "\u00A0"}</div>
                        <div style={{ borderTop: "1px solid #D8DDEB", marginTop: "4px", paddingTop: "4px" }}>
                          {accessionSlots[firstIndex + 1]?.trim() || "\u00A0"}
                        </div>
                      </th>
                    );
                  })
                  : accessionColumnSpans.map((span, i) => (
                    <th
                      key={`acc-col-group-${row.id}-${i}`}
                      colSpan={span}
                      style={plateHeaderCellStyle}
                    >
                      {accessionSlots[i]?.trim() || "\u00A0"}
                    </th>
                  ))}
              </tr>
              <tr>
                {columns.map((column) => (
                  <th key={column} style={plateHeaderCellStyle}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((rowLabel) => (
                <tr key={rowLabel}>
                  <th style={plateRowHeaderCellStyle}>{rowLabel}</th>
                  {columns.map((column) => {
                    const wellKey = normalizeWellKey(`${rowLabel}${column}`);
                    const detail = wellLookup.get(wellKey);
                    const fromTests = formatAssignTestCellText(
                      detail?.assignTestJson,
                    );
                    const cellText = fromTests || "";
                    return (
                      <td key={wellKey} style={plateWellCellStyle}>
                        <div>{cellText}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RunDetailPanel;
